import React from 'react';
import { format } from 'date-fns';
import { v4 as uuid } from 'uuid';

import { DisplayTimes } from '../../..';
import { ScheduleSpecialist } from '../../../../../models/ScheduleSpecialist';
import { fetchSlotsSpecialistDates, fetchSlotsSpecialistHours } from '../../../../../modules/slots';
import { daysToEndMonth, getRangeDateFromToday } from '../../../../../utils/dates';

export type useSpecialistHoursProps = {
  selectedItemToSchedule: string;
  datePickerMonth?: Date;
  setDisplayDatesPicker: (value: React.SetStateAction<Date[] | undefined>) => void;
  scheduleSpecialist: ScheduleSpecialist;
  setLoading: (value: boolean) => void;
  setSelectedDate: (value: Date | null) => void;
  addSlotTimes: (scheduleSlots: DisplayTimes[]) => void;
  setDisplayDates: (value: Date[]) => void;
  setInterval: (value: number) => void;
  onSelectTime: (value: DisplayTimes | undefined) => void;
};

export default function useSpecialistHours({
  selectedItemToSchedule,
  scheduleSpecialist,
  setDisplayDates,
  datePickerMonth,
  setDisplayDatesPicker,
  setLoading,
  setSelectedDate,
  addSlotTimes,
  setInterval,
  onSelectTime
}: useSpecialistHoursProps) {
  const [specialistDates, setSpecialistDates] = React.useState<string[]>([]);
  const [specialistSlots, setSpecialistSlots] = React.useState<string[]>([]);
  const { resource, appointmentType, unit, attendanceType, payment } = scheduleSpecialist;

  function addSpecialistSlotsList(slots: string[]) {
    let newSpecialistSlots = [] as string[];

    newSpecialistSlots = specialistDates.filter(item => {
      if (slots.find(specialistSlots => specialistSlots === item)) return undefined;
      return item;
    });

    newSpecialistSlots = [...newSpecialistSlots, ...slots];
    newSpecialistSlots.sort((date1, date2) => {
      if (new Date(date1 + 'T00:00:00') > new Date(date2 + 'T00:00:00')) return 1;
      return -1;
    });

    setSpecialistDates(newSpecialistSlots);
  }

  async function getSpecialistSlots(range: { startDate: Date; endDate: Date; isFirstLoad?: boolean }) {
    setLoading(true);
    onSelectTime(undefined);

    if (range.startDate < new Date()) range.startDate = new Date();

    const dateHoursResult = await fetchSlotsSpecialistDates({
      professionalId: Number(resource.professionalId),
      companyId: resource.companyId,
      medicalProcedureIds: Number(resource.medicalProcedureId),
      startDate: format(range.startDate, 'yyyy-MM-dd'),
      endDate: format(range.endDate, 'yyyy-MM-dd'),
      specialistId: resource.specialistId,
      officeId: unit.officeId,
      attendanceType: attendanceType || '',
      appointmentType: appointmentType || '',
      findThreeDates: true,
      ...(payment?.healthPlan?.id ? { healthPlanId: payment.healthPlan.id } : {})
    });

    if (dateHoursResult?.data) {
      convertSlotsToDisplayDates(dateHoursResult.data);
      addSpecialistSlotsList(dateHoursResult.data);

      if (range.isFirstLoad && dateHoursResult.data.length) {
        saveThreeDates(dateHoursResult.data);
        const [firstDate] = dateHoursResult.data;

        const convertedDate = new Date(firstDate + 'T00:00:00');

        setSelectedDate(convertedDate);
        await getSpecialistTimes(convertedDate);
      }
    }
    setLoading(false);
  }

  async function getSlotsUntilEndMonth(initRangeDate: Date) {
    const daysUntilEndMonth = daysToEndMonth(initRangeDate) - new Date().getDate();
    let endDate = new Date();

    if (daysUntilEndMonth > 3) {
      endDate = getRangeDateFromToday(daysUntilEndMonth).endDate;
    } else {
      const dateInitCopy = new Date(initRangeDate);
      dateInitCopy.setMonth(initRangeDate.getMonth() + 1);

      dateInitCopy.setDate(daysToEndMonth(dateInitCopy));

      endDate = dateInitCopy;
    }

    await getSpecialistSlots({ startDate: initRangeDate, endDate: new Date(endDate), isFirstLoad: true });
  }

  async function getSlotsUntilNextMonth(monthToRange: Date) {
    const startDate = new Date(monthToRange);
    startDate.setDate(1);

    const endDate = new Date(monthToRange);
    endDate.setDate(daysToEndMonth(monthToRange));

    await getSpecialistSlots({ startDate, endDate: new Date(endDate) });
  }

  function saveThreeDates(slots: string[]) {
    const dates = slots.map(item => new Date(item + 'T00:00:00'));
    setDisplayDates(dates.slice(0, 3));
  }

  function convertSlotsToDisplayDates(slots: string[]) {
    const dates = slots.map(item => new Date(item + 'T00:00:00'));
    setDisplayDatesPicker(dates);
  }

  async function getSpecialistTimes(date: Date) {
    onSelectTime(undefined);
    if (datePickerMonth && date.getMonth() !== datePickerMonth.getMonth()) await getSlotsUntilNextMonth(date);

    setLoading(true);

    const dateHoursResult = await fetchSlotsSpecialistHours({
      professionalId: Number(resource.professionalId),
      companyId: resource.companyId,
      medicalProcedureIds: Number(resource.medicalProcedureId),
      date: format(date, 'yyyy-MM-dd'),
      specialistId: resource.specialistId,
      officeId: unit.officeId,
      attendanceType: attendanceType || '',
      appointmentType: appointmentType || '',
      ...(payment?.healthPlan?.id ? { healthPlanId: payment.healthPlan.id } : {})
    });

    if (dateHoursResult?.data) {
      setInterval(dateHoursResult.data?.interval);
      setSpecialistSlots(dateHoursResult.data?.hours || []);

      const slotsTimes = [] as DisplayTimes[];

      dateHoursResult.data?.hours?.map(hour => {
        slotsTimes.push({
          id: uuid(),
          time: new Date(format(date, 'yyyy-MM-dd') + `T${hour}:00`),
          slots: []
        });
      });

      addSlotTimes(slotsTimes);
    }
    setLoading(false);
  }

  React.useEffect(() => {
    if (selectedItemToSchedule === 'SPECIALIST') getSlotsUntilEndMonth(new Date());
  }, []);

  return {
    loadSpecialistSlotsMonth: getSlotsUntilNextMonth,
    onSelectDateSpecialist: (date: Date) => getSpecialistTimes(date)
  };
}
